<template>
  <div class="pixie-daily" @click.stop>
    <div class="pixie-daily-title">
      <span>pixie daily</span>
      <div class="pixie-daily-title-actions">
        <button
          type="button"
          class="pixie-daily-header-btn"
          title="Print"
          :disabled="loading"
          @click="printNote"
        >
          <i class="fas fa-print"></i>
        </button>
        <button
          type="button"
          class="pixie-daily-header-btn"
          title="Close"
          @click="close"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
    <div class="pixie-daily-scroll">
      <div v-if="loading" class="pixie-daily-status">Loading…</div>
      <div v-else-if="error && !loaded" class="pixie-daily-status">
        {{ error }}
      </div>
      <textarea
        v-else-if="editing"
        v-model="draft"
        class="pixie-daily-editor"
        spellcheck="true"
      ></textarea>
      <div
        v-else
        class="pixie-daily-note"
        v-html="html"
        @click="openCalendarChip"
      ></div>
    </div>
    <div class="pixie-daily-bar">
      <span class="pixie-daily-meta">{{ error && loaded ? error : meta }}</span>
      <button
        type="button"
        class="pixie-daily-edit"
        :disabled="loading || saving || !loaded"
        @click="onEdit"
      >
        {{ editing ? 'Save' : 'Edit' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/axiosConfig';
import { renderNoteMarkdown } from '@/utils/noteMarkdown';
import { rewritePixieDailyEmailLinks } from '@/utils/pixieDailyEmailLink';
import { briefingMetaLine } from '@/utils/dailyBriefing';
import { calendarChipDate, calendarListPath } from '@/utils/calendarDates';
import { pixieDailyReturnLocation } from '@/utils/pixieDailyHint';

const route = useRoute();
const router = useRouter();

const emit = defineEmits(['close']);

const body = ref('');
const draft = ref('');
const updatedAt = ref(null);
const updatedByName = ref('');
const editing = ref(false);
const loading = ref(true);
const saving = ref(false);
const loaded = ref(false);
const error = ref('');

const html = computed(() => {
  const text = String(body.value || '');
  if (!text.trim()) return '<p>No pixie daily yet.</p>';
  return (
    rewritePixieDailyEmailLinks(renderNoteMarkdown(text)) ||
    '<p>Could not render pixie daily.</p>'
  );
});

const meta = computed(() =>
  briefingMetaLine(updatedByName.value, updatedAt.value),
);

function applyRow(row) {
  const data = row || {};
  body.value = data.body || '';
  draft.value = body.value;
  updatedAt.value = data.updatedAt || null;
  updatedByName.value = data.updatedByName || '';
}

onMounted(async () => {
  try {
    const response = await axios.get('/api/pixie-daily');
    applyRow(response.data);
    loaded.value = true;
  } catch (err) {
    error.value = 'Could not load pixie daily';
  } finally {
    loading.value = false;
  }
});

function close() {
  editing.value = false;
  saving.value = false;
  emit('close');
}

async function openCalendarChip(event) {
  const emailLink = event.target.closest('a.pixie-email-link');
  if (emailLink) {
    event.preventDefault();
    event.stopPropagation();
    const href = emailLink.getAttribute('href') || '';
    const query = Object.fromEntries(
      new URLSearchParams(href.split('?')[1] || ''),
    );
    router.push({ path: '/quickemail', query });
    return;
  }
  const link = event.target.closest('a[href^="#calendar-"]');
  if (!link) return;
  const chip = calendarChipDate(link.getAttribute('href'));
  const path = chip ? calendarListPath(chip) : null;
  if (!path) return;
  event.preventDefault();
  event.stopPropagation();
  await router.replace(pixieDailyReturnLocation(route.query));
  await router.push(path);
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function printNote() {
  if (loading.value) return;
  const printWindow = window.open('', '_blank', 'width=960,height=720');
  if (!printWindow) {
    window.alert('Please allow pop-ups for this site to use print preview.');
    return;
  }
  const raw = editing.value ? String(draft.value || '') : String(body.value || '');
  const bodyHtml = raw.trim()
    ? rewritePixieDailyEmailLinks(renderNoteMarkdown(raw))
    : '<p>No pixie daily yet.</p>';
  const metaHtml = escapeHtml(meta.value || '');
  const printHtml = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>pixie daily</title>
    <style>
      body {
        font-family: Arial, sans-serif;
        line-height: 1.6;
        margin: 0;
        padding: 0.5in;
        color: #333;
        font-size: 14px;
      }
      h1, h2, h3, h4, h5, h6 { color: #444; margin-top: 1.2em; margin-bottom: 0.4em; }
      h1 { font-size: 1.8em; border-bottom: 2px solid #eee; padding-bottom: 0.3em; }
      h2 { font-size: 1.5em; border-bottom: 1px solid #eee; padding-bottom: 0.3em; }
      .print-title {
        margin: 0 0 4px;
        font-size: 22px;
        color: #2c3e50;
        border: none;
        padding: 0;
      }
      .print-meta { margin: 0 0 16px; color: #999; font-size: 12px; }
      ul, ol { padding-left: 1.4em; }
      a.note-wo-chip, a[href^='#calendar-'] {
        color: #007bff;
        text-decoration: none;
        display: inline-block;
        padding: 2px 8px;
        margin: 0 1px;
        border-radius: 3px;
        background: rgba(0, 123, 255, 0.08);
        font-size: 1.2em;
        line-height: 1.3;
      }
      a[href^='tel:'] {
        color: #28a745;
        text-decoration: none;
        padding: 1px 3px;
        border-radius: 3px;
        background: rgba(40, 167, 69, 0.08);
      }
      table { border-collapse: collapse; width: 97%; font-size: 0.75em; }
      th, td { border: 1px solid #e8e8e8; padding: 1px; text-align: left; }
      th { background: #f5f5f5; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
  </head>
  <body>
    <h1 class="print-title">pixie daily</h1>
    ${metaHtml ? `<p class="print-meta">${metaHtml}</p>` : ''}
    <div>${bodyHtml}</div>
  </body>
</html>`;
  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

async function onEdit() {
  if (!loaded.value || loading.value || saving.value) return;
  if (!editing.value) {
    draft.value = body.value;
    editing.value = true;
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const response = await axios.put('/api/pixie-daily', { body: draft.value });
    applyRow(response.data);
    editing.value = false;
  } catch (err) {
    error.value = 'Could not save pixie daily';
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.pixie-daily {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #f5f3f6;
  color: #222;
}

.pixie-daily-title {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #6e5875;
  color: #fff;
  padding: 10px 12px;
  font-weight: 600;
  font-size: 1.05em;
}

.pixie-daily-title-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pixie-daily-header-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 0.95em;
}

.pixie-daily-header-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.32);
}

.pixie-daily-header-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pixie-daily-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 14px 16px 8px;
  user-select: text;
  -webkit-user-select: text;
}

.pixie-daily-scroll::-webkit-scrollbar {
  display: none;
}

.pixie-daily-note,
.pixie-daily-note * {
  user-select: text;
  -webkit-user-select: text;
}

.pixie-daily-note {
  font-size: 0.8em;
  line-height: 1.6;
  color: #333;
}

.pixie-daily-note :deep(h1),
.pixie-daily-note :deep(h2),
.pixie-daily-note :deep(h3),
.pixie-daily-note :deep(h4),
.pixie-daily-note :deep(h5),
.pixie-daily-note :deep(h6) {
  color: #444;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

.pixie-daily-note :deep(h1) {
  font-size: 1.8em;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.3em;
}

.pixie-daily-note :deep(h2) {
  font-size: 1.5em;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.3em;
}

.pixie-daily-note :deep(p) {
  margin-bottom: 1em;
}

.pixie-daily-note :deep(ul),
.pixie-daily-note :deep(ol) {
  margin-bottom: 1em;
  padding-left: 2em;
}

.pixie-daily-note :deep(li) {
  margin-bottom: 0.5em;
}

.pixie-daily-note :deep(blockquote) {
  border-left: 4px solid #ddd;
  margin: 1em 0;
  padding: 0.5em 1em;
  background: #f9f9f9;
  color: #666;
}

.pixie-daily-note :deep(code) {
  background: #f4f4f4;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
}

.pixie-daily-note :deep(pre) {
  background: #f4f4f4;
  padding: 1em;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1em 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.pixie-daily-note :deep(pre code) {
  background: transparent;
  padding: 0;
}

.pixie-daily-note :deep(table) {
  border-collapse: collapse;
  width: 97%;
  margin: 1em 0;
  table-layout: fixed;
  font-size: 0.75em;
}

.pixie-daily-note :deep(th),
.pixie-daily-note :deep(td) {
  border: 1px solid #e8e8e8;
  padding: 1px;
  text-align: left;
  word-wrap: break-word;
  min-width: 80px;
}

.pixie-daily-note :deep(th) {
  background: #f5f5f5;
  font-weight: 600;
}

.pixie-daily-note :deep(a[href^='#calendar-']),
.pixie-daily-note :deep(a.note-wo-chip) {
  color: #007bff;
  text-decoration: none;
  border: none;
  display: inline-block;
  padding: 2px 8px;
  margin: 0 1px;
  border-radius: 3px;
  transition: all 0.2s ease;
  background: rgba(0, 123, 255, 0.08);
  font-size: 1.2em;
  line-height: 1.3;
  vertical-align: baseline;
}

.pixie-daily-note :deep(a[href^='#calendar-']:hover),
.pixie-daily-note :deep(a.note-wo-chip:hover) {
  background: rgba(0, 123, 255, 0.15);
  border-bottom-color: rgba(0, 123, 255, 0.6);
  transform: translateY(-1px);
}

.pixie-daily-note :deep(a[href^='#calendar-']:active),
.pixie-daily-note :deep(a.note-wo-chip:active) {
  transform: translateY(0);
  background: rgba(0, 123, 255, 0.2);
}

.pixie-daily-note :deep(a.pixie-email-link) {
  color: #6e5875;
  font-weight: 700;
}

.pixie-daily-note :deep(a[href^='tel:']) {
  color: #28a745;
  text-decoration: none;
  border-radius: 3px;
  background: rgba(40, 167, 69, 0.08);
  font-size: 0.9em;
  padding: 1px 3px;
}

.pixie-daily-note :deep(h1:first-child),
.pixie-daily-note :deep(h2:first-child),
.pixie-daily-note :deep(h3:first-child),
.pixie-daily-note :deep(p:first-child),
.pixie-daily-note :deep(ul:first-child),
.pixie-daily-note :deep(ol:first-child) {
  margin-top: 0;
}

.pixie-daily-status {
  color: #888;
  font-style: italic;
}

.pixie-daily-editor {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 100%;
  box-sizing: border-box;
  border: 1px solid #d9d0df;
  border-radius: 6px;
  background: #fff;
  color: #222;
  font: inherit;
  line-height: 1.45;
  padding: 10px;
  resize: none;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  user-select: text;
  -webkit-user-select: text;
}

.pixie-daily-editor::-webkit-scrollbar {
  display: none;
}

.pixie-daily-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #ddd;
}

.pixie-daily-meta {
  min-width: 0;
  color: #b0b0b0;
  font-size: 0.75em;
  line-height: 1.3;
}

.pixie-daily-edit {
  flex-shrink: 0;
  height: 26px;
  padding: 0 12px;
  border: 1px solid #c4b4cc;
  border-radius: 4px;
  background: #f3eef6;
  color: #4a3a50;
  font-size: 0.8em;
  font-weight: 600;
  cursor: pointer;
}

.pixie-daily-edit:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
